import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  Table2,
  Flame,
  UserCheck,
  X,
  Plane,
  Percent,
  CheckCircle2,
} from "lucide-react";

const TABS = [
  { key: "calendar", label: "Calendar", Icon: CalendarDays },
  { key: "table", label: "Table", Icon: Table2 },
  { key: "heatmap", label: "Heatmap", Icon: Flame },
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(year, monthIndex, day) {
  const m = String(monthIndex + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}
function firstWeekday(year, monthIndex) {
  return new Date(year, monthIndex, 1).getDay();
}
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** quick helper: Set toggle */
function toggleInSet(prevSet, value) {
  const next = new Set(prevSet);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export default function AttendanceAtGlance() {
  const [mounted, setMounted] = useState(false);

  // default month/year like figma
  const [year, setYear] = useState(2024);
  const [monthIndex, setMonthIndex] = useState(11); // Dec

  const [activeTab, setActiveTab] = useState("calendar");

  // CALENDAR: multi-select days
  const [selectedDays, setSelectedDays] = useState(() => new Set([17]));
  const [anchorDay, setAnchorDay] = useState(17);

  // animations
  const [pulseKey, setPulseKey] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const lastChangedRef = useRef(new Set()); // multiple keys

  // calendar status map
  const [statusMap, setStatusMap] = useState(() => {
    const seed = {};
    seed[ymd(2024, 11, 5)] = "present";
    seed[ymd(2024, 11, 6)] = "present";
    seed[ymd(2024, 11, 7)] = "leave";
    seed[ymd(2024, 11, 8)] = "leave";
    seed[ymd(2024, 11, 12)] = "present";
    seed[ymd(2024, 11, 13)] = "half";
    seed[ymd(2024, 11, 14)] = "present";
    seed[ymd(2024, 11, 19)] = "present";
    seed[ymd(2024, 11, 20)] = "absent";
    seed[ymd(2024, 11, 24)] = "half";
    seed[ymd(2024, 11, 25)] = "present";
    seed[ymd(2024, 11, 26)] = "present";
    seed[ymd(2024, 11, 30)] = "half";
    seed[ymd(2024, 11, 31)] = "half";
    return seed;
  });

  // HEATMAP: selection + status map (per member+col)
  const heatCols = 24;
  const heatMembers = useMemo(
    () => [
      "Priya Sharma",
      "Riya Singh",
      "Karan Verma",
      "Nisha Gupta",
      "Vikram Joshi",
      "Aditi Mehta",
      "Rohit Choudhury",
      "Simran Kaur",
      "Ankit Roy",
    ],
    [],
  );

  const [heatSelected, setHeatSelected] = useState(() => new Set()); // key: `${rowIndex}:${colIndex}`
  const [heatStatus, setHeatStatus] = useState(() => {
    // seed deterministic
    const map = {};
    const rnd = (seed) => (Math.sin(seed) * 10000) % 1;
    heatMembers.forEach((_, r) => {
      for (let c = 0; c < heatCols; c++) {
        const v = rnd((r + 1) * 99 + (c + 1) * 13);
        let s = "present";
        if (v < 0.08) s = "absent";
        else if (v < 0.16) s = "leave";
        else if (v < 0.22) s = "half";
        map[`${r}:${c}`] = s;
      }
    });
    return map;
  });

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 40);
    return () => clearTimeout(t);
  }, []);

  const totalDays = useMemo(
    () => daysInMonth(year, monthIndex),
    [year, monthIndex],
  );
  const padStart = useMemo(
    () => firstWeekday(year, monthIndex),
    [year, monthIndex],
  );

  const today = new Date();
  const isThisMonth =
    today.getFullYear() === year && today.getMonth() === monthIndex;
  const todayDay = isThisMonth ? today.getDate() : null;

  const monthLabel = useMemo(
    () => `${MONTHS[monthIndex]} ${year}`,
    [monthIndex, year],
  );

  // make sure selections are valid when month changes
  useEffect(() => {
    setSelectedDays((prev) => {
      const next = new Set();
      prev.forEach((d) => {
        if (d >= 1 && d <= totalDays) next.add(d);
      });
      if (next.size === 0) next.add(clamp(anchorDay, 1, totalDays));
      return next;
    });
  }, [totalDays]); // eslint-disable-line

  const calendarStats = useMemo(() => {
    let present = 0,
      absent = 0,
      leave = 0,
      half = 0;
    for (let d = 1; d <= totalDays; d++) {
      const key = ymd(year, monthIndex, d);
      const s = statusMap[key] || "none";
      if (s === "present") present++;
      if (s === "absent") absent++;
      if (s === "leave") leave++;
      if (s === "half") half++;
    }
    const attended = present + half * 0.5;
    const pct = Math.round((attended / Math.max(totalDays, 1)) * 100);
    return { present, absent, leave, half, pct };
  }, [statusMap, totalDays, year, monthIndex]);

  // show action bar only when selection exists
  const calendarHasSelection = selectedDays.size > 0;
  const heatHasSelection = heatSelected.size > 0;

  function clearCalendarSelection() {
    setSelectedDays(new Set([anchorDay]));
    setPulseKey((k) => k + 1);
  }

  function clearHeatSelection() {
    setHeatSelected(new Set());
    setPulseKey((k) => k + 1);
  }

  /** Calendar: click day
   * - normal click toggles selection (multi select like figma)
   * - Shift click selects range from anchorDay to clicked
   */
  function onCalendarDayClick(day, e) {
    const isShift = e?.shiftKey;
    if (isShift) {
      const a = anchorDay || day;
      const start = Math.min(a, day);
      const end = Math.max(a, day);
      const next = new Set(selectedDays);
      for (let d = start; d <= end; d++) next.add(d);
      setSelectedDays(next);
    } else {
      setSelectedDays((prev) => toggleInSet(prev, day));
      setAnchorDay(day);
    }
    setPulseKey((k) => k + 1);
  }

  /** Apply status to all selected calendar days */
  function applyCalendarStatus(nextStatus) {
    const daysArray = Array.from(selectedDays);
    if (daysArray.length === 0) return;

    const changedKeys = new Set();
    setStatusMap((prev) => {
      const updated = { ...prev };
      daysArray.forEach((d) => {
        const key = ymd(year, monthIndex, d);
        updated[key] = nextStatus;
        changedKeys.add(key);
      });
      return updated;
    });

    // animation marks
    lastChangedRef.current = changedKeys;
    setPulseKey((k) => k + 1);

    // celebration for leave
    if (nextStatus === "leave") {
      setCelebrate(true);
      window.setTimeout(() => setCelebrate(false), 900);
    }
  }

  function handleMonthClick(i) {
    setMonthIndex(i);
    const newTotal = daysInMonth(year, i);

    // keep anchor valid
    const nextAnchor = clamp(anchorDay, 1, newTotal);
    setAnchorDay(nextAnchor);

    // keep selections within month
    setSelectedDays((prev) => {
      const next = new Set();
      prev.forEach((d) => {
        if (d >= 1 && d <= newTotal) next.add(d);
      });
      if (next.size === 0) next.add(nextAnchor);
      return next;
    });

    lastChangedRef.current = new Set();
    setPulseKey((k) => k + 1);
  }

  // Table demo data
  const tableRows = useMemo(
    () => [
      {
        name: "Priya Sharma",
        role: "Product Manager",
        status: "Approved",
        working: 22,
        present: 21.5,
        leave: 0,
        absent: 0,
        pct: 95,
      },
      {
        name: "Riya Singh",
        role: "Designer",
        status: "Pending",
        working: 25,
        present: 19,
        leave: 15,
        absent: 10,
        pct: 88,
      },
      {
        name: "Karan Verma",
        role: "Developer",
        status: "Pending",
        working: 24,
        present: 20.5,
        leave: 20,
        absent: 5,
        pct: 92,
      },
      {
        name: "Nisha Gupta",
        role: "QA Engineer",
        status: "Pending",
        working: 23,
        present: 22,
        leave: 30,
        absent: 15,
        pct: 90,
      },
      {
        name: "Vikram Joshi",
        role: "Team Lead",
        status: "Pending",
        working: 26,
        present: 23.5,
        leave: 25,
        absent: 10,
        pct: 87,
      },
      {
        name: "Aditi Mehta",
        role: "Data Analyst",
        status: "Approved",
        working: 27,
        present: 24.5,
        leave: 10,
        absent: 5,
        pct: 93,
      },
      {
        name: "Rohit Choudhury",
        role: "UI Designer",
        status: "Approved",
        working: 28,
        present: 25,
        leave: 12,
        absent: 8,
        pct: 86,
      },
    ],
    [],
  );

  /** Heatmap: toggle select cell */
  function onHeatCellClick(r, c, e) {
    const key = `${r}:${c}`;
    const isShift = e?.shiftKey;

    // simple shift range within same row: from last selected in row -> to new
    if (isShift) {
      // find last selected cell in same row
      let lastCol = null;
      for (const k of heatSelected) {
        const [rr, cc] = k.split(":").map(Number);
        if (rr === r) lastCol = cc;
      }
      if (lastCol == null) {
        setHeatSelected((prev) => toggleInSet(prev, key));
        return;
      }
      const start = Math.min(lastCol, c);
      const end = Math.max(lastCol, c);
      setHeatSelected((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) next.add(`${r}:${i}`);
        return next;
      });
      return;
    }

    setHeatSelected((prev) => toggleInSet(prev, key));
  }

  /** Heatmap: apply status to selected cells */
  function applyHeatStatus(nextStatus) {
    if (heatSelected.size === 0) return;

    setHeatStatus((prev) => {
      const next = { ...prev };
      heatSelected.forEach((k) => (next[k] = nextStatus));
      return next;
    });

    setPulseKey((k) => k + 1);
  }

  // UI: bottom action bars
  const calendarSelectedCount = selectedDays.size;
  const heatSelectedCount = heatSelected.size;

  return (
    <section className={`aag-wrap ${mounted ? "is-mounted" : ""}`}>
      {/* <div className="aag-bg-grad" /> */}
      {/* <div className="aag-bg-orb" /> */}

      <div className="aag-inner">
        <div className="aag-head">
          {/* <div className="aag-pill">
                        <CalendarDays className="aag-pill-icon" />
                        <span className="aag-pill-text">Visual Tracking</span>
                    </div> */}

          <h3 className="aag-title">Attendance at a Glance</h3>
          <p className="aag-subtitle">
            Interactive calendar with color-coded tracking
          </p>
        </div>

        {/* Tabs (mobile safe) */}
        <div className="aag-tabs">
          {TABS.map(({ key, label, Icon }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                className={`aag-tab ${active ? "is-active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <span className="aag-tab-bg" />
                <Icon className="aag-tab-icon" />
                <span className="aag-tab-text">{label}</span>
              </button>
            );
          })}
        </div>

        {/* Stat cards */}
        <div className="aag-stats">
          <div className="aag-stat tone-blue">
            <div className="aag-stat-left">
              <div className="aag-stat-label">Present</div>
              <div className="aag-stat-value">{calendarStats.present}</div>
            </div>
            <div className="aag-stat-iconbox tone-blue-2">
              <UserCheck className="aag-stat-icon" />
            </div>
          </div>

          <div className="aag-stat tone-red">
            <div className="aag-stat-left">
              <div className="aag-stat-label">Absent</div>
              <div className="aag-stat-value">{calendarStats.absent}</div>
            </div>
            <div className="aag-stat-iconbox tone-red-2">
              <X className="aag-stat-icon" />
            </div>
          </div>

          <div className="aag-stat tone-orange">
            <div className="aag-stat-left">
              <div className="aag-stat-label">Leave</div>
              <div className="aag-stat-value">{calendarStats.leave}</div>
            </div>
            <div className="aag-stat-iconbox tone-orange-2">
              <Plane className="aag-stat-icon" />
            </div>
          </div>

          <div className="aag-stat tone-cyan">
            <div className="aag-stat-left">
              <div className="aag-stat-label">Attendance %</div>
              <div className="aag-stat-value">{calendarStats.pct}%</div>
            </div>
            <div className="aag-stat-iconbox tone-cyan-2">
              <Percent className="aag-stat-icon" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`aag-card aag-switch`} key={`${activeTab}-${pulseKey}`}>
          {/* confetti celebration overlay */}
          <div
            className={`aag-confetti ${celebrate ? "is-on" : ""}`}
            aria-hidden="true"
          >
            {Array.from({ length: 18 }).map((_, i) => (
              <span
                key={i}
                className="aag-confetti-piece"
                style={{ "--i": i }}
              />
            ))}
          </div>

          {activeTab === "calendar" && (
            <>
              {/* Month pills */}
              <div className="aag-months">
                {MONTHS.map((m, i) => (
                  <button
                    key={m}
                    type="button"
                    className={`aag-month ${i === monthIndex ? "is-active" : ""}`}
                    onClick={() => handleMonthClick(i)}
                  >
                    <span className="aag-month-text">{m}</span>
                    {i === monthIndex && <span className="aag-month-dot" />}
                  </button>
                ))}
              </div>

              {/* Calendar header */}
              <div className="aag-cal-head">
                <h4 className="aag-cal-title">{monthLabel}</h4>

                <div className="aag-legend">
                  <LegendItem color="blue" label="Present" />
                  <LegendItem color="orange" label="Leave" />
                  <LegendItem color="red" label="Absent" />
                  <LegendItem color="half" label="Half Day" />
                </div>
              </div>

              {/* Calendar grid */}
              <div className="aag-cal-grid">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="aag-weekday">
                    {d}
                  </div>
                ))}

                {Array.from({ length: padStart }).map((_, i) => (
                  <div key={`pad-${i}`} className="aag-blank" />
                ))}

                {Array.from({ length: totalDays }).map((_, idx) => {
                  const day = idx + 1;
                  const key = ymd(year, monthIndex, day);
                  const st = statusMap[key] || "none";

                  const isSelected = selectedDays.has(day);
                  const isToday = isThisMonth && day === todayDay;
                  const wasChanged = lastChangedRef.current?.has?.(key);

                  return (
                    <button
                      key={key}
                      type="button"
                      className={[
                        "aag-day",
                        `is-${st}`,
                        isSelected ? "is-selected" : "",
                        isToday ? "is-today" : "",
                        wasChanged ? "is-changed" : "",
                      ].join(" ")}
                      onClick={(e) => onCalendarDayClick(day, e)}
                      title="Tip: Click multiple dates. Shift-click for range."
                    >
                      <span className="aag-day-ripple" aria-hidden="true" />
                      <span className="aag-day-num">{day}</span>
                    </button>
                  );
                })}
              </div>

              {/* Bottom action bar (Figma behavior) */}
              {calendarHasSelection && (
                <div className="aag-bottombar">
                  <div className="aag-bottom-left">
                    <span className="aag-selected-count">
                      {calendarSelectedCount} Selected
                    </span>
                    <button
                      className="aag-clear"
                      type="button"
                      onClick={clearCalendarSelection}
                      title="Clear selection"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="aag-bottom-actions">
                    <button
                      className="aag-bbtn is-present"
                      type="button"
                      onClick={() => applyCalendarStatus("present")}
                    >
                      Present
                    </button>
                    <button
                      className="aag-bbtn is-half"
                      type="button"
                      onClick={() => applyCalendarStatus("half")}
                    >
                      Half Day
                    </button>
                    <button
                      className="aag-bbtn is-leave"
                      type="button"
                      onClick={() => applyCalendarStatus("leave")}
                    >
                      Leave
                    </button>
                    <button
                      className="aag-bbtn is-absent"
                      type="button"
                      onClick={() => applyCalendarStatus("absent")}
                    >
                      Absent
                    </button>
                  </div>
                </div>
              )}

              <div className="aag-hint">
                <CheckCircle2 className="aag-hint-ic" />
                Tip: Click multiple dates (or Shift-click range), then press
                action button.
              </div>
            </>
          )}

          {activeTab === "table" && (
            <div className="aag-table-wrap">
              <div className="aag-table-scroll">
                <table className="aag-table">
                  <thead>
                    <tr>
                      <th>Team Member</th>
                      <th>Status</th>
                      <th>Working</th>
                      <th>Present</th>
                      <th>Leave</th>
                      <th>Absent</th>
                      <th>Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r) => (
                      <tr key={r.name}>
                        <td>
                          <div className="aag-user">
                            <span className="aag-avatar">
                              {r.name.slice(0, 1)}
                            </span>
                            <div className="aag-user-meta">
                              <div className="aag-user-name">{r.name}</div>
                              <div className="aag-user-role">{r.role}</div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`aag-badge ${r.status === "Approved" ? "is-ok" : "is-warn"}`}
                          >
                            {r.status}
                          </span>
                        </td>

                        <td className="aag-num">{r.working}</td>
                        <td className="aag-num is-green">{r.present}</td>
                        <td className="aag-num is-orange">{r.leave}</td>
                        <td className="aag-num is-red">{r.absent}</td>

                        <td>
                          <span
                            className={`aag-pillpct ${r.pct >= 90 ? "is-ok" : "is-warn"}`}
                          >
                            {r.pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="aag-table-note">
                Table is scrollable on mobile (like figma). Replace rows with
                API.
              </div>
            </div>
          )}

          {activeTab === "heatmap" && (
            <>
              <div className="aag-heat-head">
                <div className="aag-heat-title">Today - Heatmap</div>

                <div className="aag-heat-badges">
                  <span className="aag-mini is-present">Present</span>
                  <span className="aag-mini is-half">Half Day</span>
                  <span className="aag-mini is-absent">Absent</span>
                  <span className="aag-mini is-leave">Leave</span>
                  <span className="aag-mini is-none">No Data</span>
                </div>
              </div>

              <div className="aag-heat-grid">
                <div className="aag-heat-cols">
                  {Array.from({ length: heatCols }).map((_, i) => (
                    <div className="aag-heat-colnum" key={i}>
                      {i + 1}
                    </div>
                  ))}
                </div>

                {heatMembers.map((member, r) => (
                  <div className="aag-heat-row" key={member}>
                    <div className="aag-heat-name">
                      <span className="aag-avatar sm">
                        {member.slice(0, 1)}
                      </span>
                      <span className="aag-heat-member">{member}</span>
                    </div>

                    <div className="aag-heat-cells">
                      {Array.from({ length: heatCols }).map((_, c) => {
                        const k = `${r}:${c}`;
                        const s = heatStatus[k] || "none";
                        const isSel = heatSelected.has(k);

                        return (
                          <button
                            key={k}
                            type="button"
                            className={`aag-heat-cell is-${s} ${isSel ? "is-selected" : ""}`}
                            onClick={(e) => onHeatCellClick(r, c, e)}
                            title="Tip: click multiple cells (Shift for row-range)"
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Heatmap bottom bar (like figma screenshot) */}
              {heatHasSelection && (
                <div className="aag-bottombar">
                  <div className="aag-bottom-left">
                    <span className="aag-selected-count">
                      {heatSelectedCount} Selected
                    </span>
                    <button
                      className="aag-clear"
                      type="button"
                      onClick={clearHeatSelection}
                      title="Clear selection"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="aag-bottom-actions">
                    <button
                      className="aag-bbtn is-present"
                      type="button"
                      onClick={() => applyHeatStatus("present")}
                    >
                      Present
                    </button>
                    <button
                      className="aag-bbtn is-half"
                      type="button"
                      onClick={() => applyHeatStatus("half")}
                    >
                      Half Day
                    </button>
                    <button
                      className="aag-bbtn is-absent"
                      type="button"
                      onClick={() => applyHeatStatus("absent")}
                    >
                      Absent
                    </button>
                  </div>
                </div>
              )}

              <div className="aag-table-note">
                Heatmap supports multi-select + apply actions.
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="aag-leg-item">
      <span className={`aag-leg-box is-${color}`} />
      <span className="aag-leg-text">{label}</span>
    </div>
  );
}
